import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, Star } from "lucide-react";
import { adminAxios, formatApiError } from "../../lib/adminApi";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ author: "", rating: 5, time: "Recently", text: "" });

  const load = async () => {
    try {
      const { data } = await adminAxios.get("/admin/reviews");
      setItems(data.reviews || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.author.trim() || !form.text.trim()) {
      toast.error("Author and review text required");
      return;
    }
    setAdding(true);
    try {
      await adminAxios.post("/admin/reviews", form);
      toast.success("Review added");
      setForm({ author: "", rating: 5, time: "Recently", text: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const save = async (r) => {
    try {
      await adminAxios.put(`/admin/reviews/${r.id}`, {
        author: r.author, rating: r.rating, time: r.time, text: r.text,
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await adminAxios.delete(`/admin/reviews/${id}`);
      toast.success("Deleted");
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-reviews-page" className="space-y-6">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Reviews</h1>
        <p className="text-slate-500 text-sm mt-1">Manage the Google-style reviews shown on your Home page.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 card-soft">
        <h2 className="font-semibold text-slate-900 mb-3">Add new review</h2>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <div className="space-y-1.5">
            <Label>Author</Label>
            <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="review-new-author" placeholder="Customer name" />
          </div>
          <div className="space-y-1.5">
            <Label>Rating</Label>
            <select
              className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm"
              value={form.rating}
              onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })}
              data-testid="review-new-rating"
            >
              {[5, 4, 3, 2, 1].map((n) => (<option key={n} value={n}>{n} ★</option>))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Time</Label>
            <Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="review-new-time" placeholder="e.g. 2 weeks ago" />
          </div>
        </div>
        <div className="space-y-1.5 mb-3">
          <Label>Review text</Label>
          <Textarea rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-new-text" placeholder="What the customer said..." />
        </div>
        <button onClick={add} disabled={adding} data-testid="review-add-btn" className="h-10 px-5 rounded-full brand-btn text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add review
        </button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#1C6B8A]" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="reviews-list">
          {items.map((r, idx) => (
            <ReviewEditor key={r.id} initial={r} onSave={save} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewEditor({ initial, onSave, onDelete }) {
  const [r, setR] = useState(initial);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 card-soft" data-testid={`review-card-${r.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1C6B8A] to-[#155670] text-white text-sm font-semibold flex items-center justify-center">
          {r.initial || r.author?.slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1">
          <Input value={r.author} onChange={(e) => setR({ ...r, author: e.target.value })} className="text-sm" data-testid={`review-author-${r.id}`} />
        </div>
        <select
          className="h-9 rounded-md border border-slate-200 px-2 text-sm"
          value={r.rating}
          onChange={(e) => setR({ ...r, rating: parseInt(e.target.value) })}
          data-testid={`review-rating-${r.id}`}
        >
          {[5, 4, 3, 2, 1].map((n) => (<option key={n} value={n}>{n}★</option>))}
        </select>
      </div>
      <Input value={r.time || ""} onChange={(e) => setR({ ...r, time: e.target.value })} className="text-xs mb-2" placeholder="Time" />
      <Textarea rows={3} value={r.text} onChange={(e) => setR({ ...r, text: e.target.value })} className="text-sm" data-testid={`review-text-${r.id}`} />
      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => onSave(r)} data-testid={`review-save-${r.id}`} className="flex-1 h-9 rounded-full brand-btn text-xs font-semibold inline-flex items-center justify-center gap-1.5">
          <Save className="w-3.5 h-3.5" /> Save
        </button>
        <button onClick={() => onDelete(r.id)} data-testid={`review-delete-${r.id}`} className="h-9 w-9 rounded-full border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
